import moment from 'moment-timezone';

/**
 * Generates a Google Calendar URL for adding an event
 * @param {Object} booking - The booking object
 * @param {string} orgTimeZone - Organization timezone
 * @returns {string} Google Calendar URL
 */
export function generateGoogleCalendarUrl(booking, orgTimeZone) {
  if (!booking || !booking.startTime || !booking.endTime) {
    throw new Error('Invalid booking data');
  }

  // Convert times to UTC for Google Calendar (required format)
  const startUtc = moment.tz(booking.startTime, orgTimeZone).utc();
  const endUtc = moment.tz(booking.endTime, orgTimeZone).utc();

  // Format dates as YYYYMMDDTHHMMSSZ (Google Calendar format)
  const startFormatted = startUtc.format('YYYYMMDD[T]HHmmss[Z]');
  const endFormatted = endUtc.format('YYYYMMDD[T]HHmmss[Z]');

  // Prepare event details
  const title = `Meeting - ${booking.room?.name || 'Room'}`;
  const location = booking.room?.name || 'Meeting Room';
  const description = [
    `Booking ID: ${booking._id}`,
    `Room: ${booking.room?.name || 'N/A'}`,
    `Start: ${moment.tz(booking.startTime, orgTimeZone).format('MMM DD, YYYY [at] h:mm A')} (${orgTimeZone})`,
    `End: ${moment.tz(booking.endTime, orgTimeZone).format('MMM DD, YYYY [at] h:mm A')} (${orgTimeZone})`,
    '',
    'This event was created from the Meeting Room Booking System.'
  ].join('\n');

  // Build Google Calendar URL
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${startFormatted}/${endFormatted}`,
    details: description,
    location: location
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Generates ICS file content for calendar import
 * @param {Object} booking - The booking object
 * @param {string} orgTimeZone - Organization timezone
 * @returns {string} ICS file content
 */
export function generateICSContent(booking, orgTimeZone) {
  if (!booking || !booking.startTime || !booking.endTime) {
    throw new Error('Invalid booking data');
  }

  // Convert times to UTC for ICS format
  const startUtc = moment.tz(booking.startTime, orgTimeZone).utc();
  const endUtc = moment.tz(booking.endTime, orgTimeZone).utc();

  // Format dates as YYYYMMDDTHHMMSSZ (ICS format)
  const startFormatted = startUtc.format('YYYYMMDD[T]HHmmss[Z]');
  const endFormatted = endUtc.format('YYYYMMDD[T]HHmmss[Z]');
  const nowFormatted = moment.utc().format('YYYYMMDD[T]HHmmss[Z]');

  // Prepare event details
  const title = `Meeting - ${booking.room?.name || 'Room'}`;
  const location = booking.room?.name || 'Meeting Room';
  const description = [
    `Booking ID: ${booking._id}`,
    `Room: ${booking.room?.name || 'N/A'}`,
    `This event was created from the Meeting Room Booking System.`
  ].join('\\n');

  // Generate unique ID
  const uid = `booking-${booking._id}@meetingroom.local`;

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Meeting Room System//Meeting Room Booking//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTART:${startFormatted}`,
    `DTEND:${endFormatted}`,
    `DTSTAMP:${nowFormatted}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${description}`,
    `LOCATION:${location}`,
    'STATUS:CONFIRMED',
    'SEQUENCE:0',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');

  return icsContent;
}

/**
 * Downloads an ICS file
 * @param {Object} booking - The booking object
 * @param {string} orgTimeZone - Organization timezone
 */
export function downloadICSFile(booking, orgTimeZone) {
  try {
    const icsContent = generateICSContent(booking, orgTimeZone);
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    
    // Create temporary download link
    const link = document.createElement('a');
    link.href = url;
    link.download = `meeting-${booking.room?.name || 'room'}-${moment.tz(booking.startTime, orgTimeZone).format('YYYY-MM-DD')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error generating ICS file:', error);
    throw error;
  }
}

/**
 * Generates Outlook Calendar URL for adding an event
 * @param {Object} booking - The booking object
 * @param {string} orgTimeZone - Organization timezone
 * @returns {string} Outlook Calendar URL
 */
export function generateOutlookCalendarUrl(booking, orgTimeZone) {
  if (!booking || !booking.startTime || !booking.endTime) {
    throw new Error('Invalid booking data');
  }

  // Convert times to UTC for Outlook Calendar
  const startUtc = moment.tz(booking.startTime, orgTimeZone).utc();
  const endUtc = moment.tz(booking.endTime, orgTimeZone).utc();

  // Format dates as ISO string for Outlook
  const startFormatted = startUtc.toISOString();
  const endFormatted = endUtc.toISOString();

  // Prepare event details
  const subject = `Meeting - ${booking.room?.name || 'Room'}`;
  const location = booking.room?.name || 'Meeting Room';
  const body = [
    `Booking ID: ${booking._id}`,
    `Room: ${booking.room?.name || 'N/A'}`,
    `Start: ${moment.tz(booking.startTime, orgTimeZone).format('MMM DD, YYYY [at] h:mm A')} (${orgTimeZone})`,
    `End: ${moment.tz(booking.endTime, orgTimeZone).format('MMM DD, YYYY [at] h:mm A')} (${orgTimeZone})`,
    '',
    'This event was created from the Meeting Room Booking System.'
  ].join('%0A');

  // Build Outlook Calendar URL
  const params = new URLSearchParams({
    subject: subject,
    startdt: startFormatted,
    enddt: endFormatted,
    body: body,
    location: location,
    path: '/calendar/action/compose',
    rru: 'addevent'
  });

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

/**
 * Generates Yahoo Calendar URL for adding an event
 * @param {Object} booking - The booking object
 * @param {string} orgTimeZone - Organization timezone
 * @returns {string} Yahoo Calendar URL
 */
export function generateYahooCalendarUrl(booking, orgTimeZone) {
  if (!booking || !booking.startTime || !booking.endTime) {
    throw new Error('Invalid booking data');
  }

  // Convert times to UTC for Yahoo Calendar
  const startUtc = moment.tz(booking.startTime, orgTimeZone).utc();
  const endUtc = moment.tz(booking.endTime, orgTimeZone).utc();

  // Format dates as YYYYMMDDTHHMMSSZ for Yahoo
  const startFormatted = startUtc.format('YYYYMMDD[T]HHmmss[Z]');
  const endFormatted = endUtc.format('YYYYMMDD[T]HHmmss[Z]');

  // Prepare event details
  const title = `Meeting - ${booking.room?.name || 'Room'}`;
  const location = booking.room?.name || 'Meeting Room';
  const desc = [
    `Booking ID: ${booking._id}`,
    `Room: ${booking.room?.name || 'N/A'}`,
    `This event was created from the Meeting Room Booking System.`
  ].join('\n');

  // Build Yahoo Calendar URL
  const params = new URLSearchParams({
    v: '60',
    title: title,
    st: startFormatted,
    et: endFormatted,
    desc: desc,
    in_loc: location
  });

  return `https://calendar.yahoo.com/?${params.toString()}`;
}

/**
 * Generates Apple iCloud Calendar URL for adding an event
 * @param {Object} booking - The booking object
 * @param {string} orgTimeZone - Organization timezone
 * @returns {string} Apple Calendar URL
 */
export function generateAppleCalendarUrl(booking, orgTimeZone) {
  if (!booking || !booking.startTime || !booking.endTime) {
    throw new Error('Invalid booking data');
  }

  // Convert times to UTC for Apple Calendar
  const startUtc = moment.tz(booking.startTime, orgTimeZone).utc();
  const endUtc = moment.tz(booking.endTime, orgTimeZone).utc();

  // Format dates as YYYYMMDDTHHMMSSZ for Apple
  const startFormatted = startUtc.format('YYYYMMDD[T]HHmmss[Z]');
  const endFormatted = endUtc.format('YYYYMMDD[T]HHmmss[Z]');

  // Prepare event details
  const title = `Meeting - ${booking.room?.name || 'Room'}`;
  const location = booking.room?.name || 'Meeting Room';
  const details = [
    `Booking ID: ${booking._id}`,
    `Room: ${booking.room?.name || 'N/A'}`,
    `This event was created from the Meeting Room Booking System.`
  ].join('\n');

  // Build Apple Calendar URL (uses webcal:// protocol or https for iCloud)
  const params = new URLSearchParams({
    title: title,
    startdt: startFormatted,
    enddt: endFormatted,
    location: location,
    details: details
  });

  return `https://www.icloud.com/calendar/new?${params.toString()}`;
}

/**
 * Opens Google Calendar with pre-filled event
 * @param {Object} booking - The booking object
 * @param {string} orgTimeZone - Organization timezone
 */
export function addToGoogleCalendar(booking, orgTimeZone) {
  try {
    const url = generateGoogleCalendarUrl(booking, orgTimeZone);
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    console.error('Error opening Google Calendar:', error);
    throw error;
  }
}

/**
 * Opens Outlook Calendar with pre-filled event
 * @param {Object} booking - The booking object
 * @param {string} orgTimeZone - Organization timezone
 */
export function addToOutlookCalendar(booking, orgTimeZone) {
  try {
    const url = generateOutlookCalendarUrl(booking, orgTimeZone);
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    console.error('Error opening Outlook Calendar:', error);
    throw error;
  }
}

/**
 * Opens Yahoo Calendar with pre-filled event
 * @param {Object} booking - The booking object
 * @param {string} orgTimeZone - Organization timezone
 */
export function addToYahooCalendar(booking, orgTimeZone) {
  try {
    const url = generateYahooCalendarUrl(booking, orgTimeZone);
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    console.error('Error opening Yahoo Calendar:', error);
    throw error;
  }
}

/**
 * Opens Apple Calendar with pre-filled event
 * @param {Object} booking - The booking object
 * @param {string} orgTimeZone - Organization timezone
 */
export function addToAppleCalendar(booking, orgTimeZone) {
  try {
    const url = generateAppleCalendarUrl(booking, orgTimeZone);
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    console.error('Error opening Apple Calendar:', error);
    throw error;
  }
} 